import { useEffect, useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../lib/auth';
import {
  adminGetSalesDashboard,
  adminGetSalesAll,
  adminGetSalesByUser,
  adminGetSalesByRaffle,
  adminResyncTransaction,
  adminSyncAllPending,
  adminForceFulfilTransaction,
  adminUpdateTransactionMethod,
  getAdminRaffles,
} from '../../lib/api';
import AdminSidebar from '../../components/AdminSidebar';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../../components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import {
  TrendingUp, TrendingDown, DollarSign, ShoppingBag, Search, Ticket, Coins, Crown,
  Loader2, RotateCw, Award, BarChart3, AlertCircle, Check, Clock, XCircle,
  User as UserIcon, Eye, Download, Trophy, Users as UsersIcon, Filter, RefreshCw,
  ShieldCheck, Zap, CreditCard, Wallet, ArrowUpRight, ArrowDownRight, Sparkles,
  Calendar, Activity, Mail, Phone, ChevronUp, ChevronDown, FileText, Save, Edit3,
} from 'lucide-react';
import { toast } from 'sonner';

// ─── Constants ────────────────────────────────────────────────
const TYPE_META = {
  point_pack:    { label: 'Recharge',  icon: Coins,       color: 'blue',    bg: 'bg-blue-100',    text: 'text-blue-700',    border: 'border-blue-200' },
  vip:           { label: 'VIP',       icon: Crown,       color: 'gold',    bg: 'bg-gold/20',     text: 'text-gold-dark',   border: 'border-gold/40' },
  raffle_direct: { label: 'Tickets (carte)', icon: Ticket, color: 'emerald', bg: 'bg-emerald-100', text: 'text-emerald-700', border: 'border-emerald-200' },
  raffle_credit: { label: 'Tickets (crédit)', icon: Ticket, color: 'amber',  bg: 'bg-amber-100',   text: 'text-amber-700',   border: 'border-amber-200' },
  manual_credit: { label: 'Vente manuelle', icon: Sparkles, color: 'purple', bg: 'bg-purple-100',  text: 'text-purple-700',  border: 'border-purple-200' },
};

// Crypto transactions reuse the underlying type (point_pack/vip/raffle_direct) but
// have gateway_mode='nowpayments'. We surface this distinction in the UI via
// payment method badges (carte / crédit / crypto).

// French label for a payment method key (used in the editable dialog).
function methodLabelFr(key) {
  const map = {
    cash: 'Espèces',
    bank_transfer: 'Virement bancaire',
    card: 'Carte bancaire',
    crypto: 'Crypto',
    other: 'Autre',
    offert: 'Offert',
    gift: 'Offert',
  };
  return map[String(key || '').toLowerCase()] || 'Manuel';
}

// Returns a short uppercase label describing how the customer actually paid.
// Reads gateway_mode + metadata.payment_method (for manual credits) + amount_gbp.
function paymentMethodLabel(t) {
  const gateway = t?.gateway_mode || 'direct';
  const meta = t?.metadata || {};
  if (gateway === 'nowpayments') return 'crypto';
  if (gateway === 'manual') {
    const method = (meta.payment_method || '').toLowerCase();
    const map = {
      cash: 'espèces',
      bank_transfer: 'virement',
      card: 'carte CB',
      crypto: 'crypto',
      other: 'autre',
      offert: 'offert',
      gift: 'offert',
    };
    return map[method] || 'manuel';
  }
  if (Number(t?.amount_gbp || 0) > 0) return 'carte';
  return 'crédit';
}

// Returns the typeMeta tweaked for the gateway used (e.g. raffle_direct paid in
// crypto displays "Tickets (crypto)" with an orange badge instead of "carte").
function getTypeMeta(t) {
  const base = TYPE_META[t.type] || TYPE_META.point_pack;
  const gateway = t.gateway_mode || 'direct';
  if (gateway === 'nowpayments') {
    return {
      ...base,
      label: t.type === 'raffle_direct' ? 'Tickets (crypto)' : (t.type === 'point_pack' ? 'Recharge (crypto)' : t.type === 'vip' ? 'VIP (crypto)' : base.label),
      bg: 'bg-orange-100',
      text: 'text-orange-700',
      border: 'border-orange-200',
    };
  }
  if (gateway === 'manual') {
    return {
      ...base,
      label: 'Vente manuelle',
      bg: 'bg-purple-100',
      text: 'text-purple-700',
      border: 'border-purple-200',
    };
  }
  return base;
}

const STATUS_META = {
  paid:      { label: 'Payé',       cls: 'bg-emerald-500',  textCls: 'text-emerald-700',  bgSoft: 'bg-emerald-50', icon: Check },
  unpaid:    { label: 'En attente', cls: 'bg-amber-500',    textCls: 'text-amber-700',    bgSoft: 'bg-amber-50',   icon: Clock },
  expired:   { label: 'Expiré',     cls: 'bg-stone-400',    textCls: 'text-stone-700',    bgSoft: 'bg-stone-50',   icon: XCircle },
  failed:    { label: 'Échoué',     cls: 'bg-red-500',      textCls: 'text-red-700',      bgSoft: 'bg-red-50',     icon: XCircle },
  cancelled: { label: 'Annulé',     cls: 'bg-stone-500',    textCls: 'text-stone-700',    bgSoft: 'bg-stone-50',   icon: XCircle },
};

// ─── Helpers ──────────────────────────────────────────────────
const fmt = (v) => `£${Number(v || 0).toLocaleString('en-GB', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const fmtCompact = (v) => {
  const n = Number(v || 0);
  if (n >= 1_000_000) return `£${(n / 1_000_000).toFixed(1)}M`;
  if (n >= 10_000)    return `£${(n / 1_000).toFixed(1)}k`;
  return fmt(n);
};
const fmtDate = (d) => (d ? new Date(d).toLocaleString('fr-FR') : '—');
const fmtRelative = (d) => {
  if (!d) return '—';
  const diff = (Date.now() - new Date(d).getTime()) / 1000;
  if (diff < 60) return 'à l\u2019instant';
  if (diff < 3600) return `il y a ${Math.floor(diff / 60)} min`;
  if (diff < 86400) return `il y a ${Math.floor(diff / 3600)} h`;
  if (diff < 86400 * 7) return `il y a ${Math.floor(diff / 86400)} j`;
  return new Date(d).toLocaleDateString('fr-FR');
};

const getStatusKey = (t) => {
  if (t.fulfilled) return 'paid';
  if (t.status === 'expired') return 'expired';
  if (t.status === 'failed' || t.payment_status === 'failed') return 'failed';
  if (t.status === 'cancelled') return 'cancelled';
  return 'unpaid';
};

// Initials avatar with deterministic color
function userColor(seed = '') {
  const colors = ['bg-emerald-500', 'bg-blue-500', 'bg-purple-500', 'bg-pink-500', 'bg-amber-500', 'bg-cyan-500', 'bg-rose-500', 'bg-indigo-500'];
  let hash = 0;
  for (let i = 0; i < seed.length; i++) hash = (hash * 31 + seed.charCodeAt(i)) | 0;
  return colors[Math.abs(hash) % colors.length];
}
function initials(name = '', email = '') {
  const src = (name || email || '').trim();
  if (!src) return '?';
  const parts = src.split(/\s+/);
  if (parts.length >= 2) return (parts[0][0] + parts[1][0]).toUpperCase();
  return src.substring(0, 2).toUpperCase();
}

// ─── Main component ──────────────────────────────────────────
export default function AdminSales() {
  const { user, isAdmin, loading } = useAuth();
  const navigate = useNavigate();

  const [data, setData] = useState(null);
  const [transactions, setTransactions] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [refreshing, setRefreshing] = useState(false);
  const [autoRefresh, setAutoRefresh] = useState(true);
  const [lastRefresh, setLastRefresh] = useState(null);

  const [activeTab, setActiveTab] = useState('overview');
  const [filterStatus, setFilterStatus] = useState('all');
  const [filterType, setFilterType] = useState('all');
  const [filterRaffleId, setFilterRaffleId] = useState('all');
  // Dashboard-level raffle filter (top of page, applies to KPIs).
  // Initialised from localStorage so the admin's preferred default raffle
  // is restored on every page visit.
  const DASHBOARD_FILTER_STORAGE_KEY = 'winluxor_admin_sales_default_raffle';
  const [dashboardRaffleId, setDashboardRaffleId] = useState(() => {
    if (typeof window === 'undefined') return 'all';
    try { return localStorage.getItem(DASHBOARD_FILTER_STORAGE_KEY) || 'all'; }
    catch { return 'all'; }
  });
  const [searchTerm, setSearchTerm] = useState('');

  // Sorting (client-side)
  const [sortField, setSortField] = useState('created_at');
  const [sortDir, setSortDir] = useState('desc');

  const [selectedTxn, setSelectedTxn] = useState(null);
  const [userDrillDown, setUserDrillDown] = useState(null);
  const [allRaffles, setAllRaffles] = useState([]);
  const [perRaffle, setPerRaffle] = useState(null);
  const [loadingPerRaffle, setLoadingPerRaffle] = useState(false);
  const [selectedRaffle, setSelectedRaffle] = useState(null);
  const [syncingAll, setSyncingAll] = useState(false);
  const [resyncingId, setResyncingId] = useState(null);

  // ── Effects ────────────────────────────────────────────────
  useEffect(() => {
    if (loading) return;
    if (!user || !isAdmin) { navigate('/admin/login'); return; }
    loadDashboard();
    loadRafflesList();
    let interval = null;
    if (autoRefresh) {
      interval = setInterval(() => {
        loadDashboard(true);
        if (activeTab === 'transactions') loadTransactions(true);
        if (activeTab === 'by-raffle') loadPerRaffle(true);
      }, 30000);
    }
    return () => { if (interval) clearInterval(interval); };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, isAdmin, loading, autoRefresh, activeTab, dashboardRaffleId]);

  useEffect(() => {
    if (activeTab === 'transactions') loadTransactions();
    else if (activeTab === 'by-raffle') loadPerRaffle();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab, filterStatus, filterType, filterRaffleId, searchTerm]);

  // ── Data loaders ───────────────────────────────────────────
  const loadDashboard = async (silent = false) => {
    if (!silent) setRefreshing(true);
    try {
      const res = await adminGetSalesDashboard(dashboardRaffleId);
      setData(res.data);
      setLastRefresh(new Date());
    } catch {
      if (!silent) toast.error('Erreur de chargement');
    } finally {
      if (!silent) setRefreshing(false);
    }
  };

  const loadTransactions = async (silent = false) => {
    if (!silent) setRefreshing(true);
    try {
      const params = { limit: 300 };
      if (filterStatus !== 'all') params.status = filterStatus;
      if (filterType !== 'all') params.type_filter = filterType;
      if (searchTerm.trim()) params.user_email = searchTerm.trim();
      if (filterRaffleId && filterRaffleId !== 'all') params.raffle_id = filterRaffleId;
      const res = await adminGetSalesAll(params);
      setTransactions(res.data.transactions || []);
      setTotalCount(res.data.total || 0);
    } catch {
      if (!silent) toast.error('Erreur de chargement');
    } finally {
      if (!silent) setRefreshing(false);
    }
  };

  const loadRafflesList = async () => {
    try {
      const res = await getAdminRaffles();
      const list = Array.isArray(res.data) ? res.data : (res.data?.raffles || []);
      setAllRaffles(list);
    } catch { /* ignore */ }
  };

  const loadPerRaffle = async (silent = false) => {
    if (!silent) setLoadingPerRaffle(true);
    try {
      const res = await adminGetSalesByRaffle();
      setPerRaffle(res.data);
    } catch {
      if (!silent) toast.error('Erreur de chargement par tirage');
    } finally {
      if (!silent) setLoadingPerRaffle(false);
    }
  };

  // ── Actions ────────────────────────────────────────────────
  const handleViewUser = async (userId) => {
    if (!userId) return;
    try {
      const res = await adminGetSalesByUser(userId);
      setUserDrillDown(res.data);
    } catch (e) { toast.error(e?.response?.data?.detail || 'Erreur'); }
  };

  const handleResync = async (sessionId) => {
    if (!sessionId) return;
    setResyncingId(sessionId);
    try {
      const res = await adminResyncTransaction(sessionId);
      const s = res.data?.status;
      if (s === 'fulfilled') toast.success('Paiement récupéré ! Ticket créé + email envoyé ✅');
      else if (s === 'already_fulfilled') toast.info('Déjà validée');
      else if (s === 'still_pending') toast.warning(`Toujours en attente (${res.data?.detail || '—'})`);
      else if (s === 'failed') toast.error(`Paiement échoué (${res.data?.detail || '—'})`);
      else if (s === 'provider_error') toast.error(`Erreur Luxomarket: ${res.data?.detail || res.data?.http_status || '—'}`);
      else toast.info(`Statut: ${s || 'inconnu'}`);
      await loadDashboard(true);
      if (activeTab === 'transactions') await loadTransactions(true);
    } catch (e) { toast.error(e?.response?.data?.detail || 'Erreur'); }
    finally { setResyncingId(null); }
  };

  const handleForceFulfil = async (sessionId) => {
    if (!sessionId) return;
    if (!window.confirm('⚠️ Forcer la validation ?\n\nUtilisez UNIQUEMENT si vous avez confirmé le paiement manuellement (Stripe Dashboard).\n\nLes tickets seront créés et un email envoyé.')) return;
    setResyncingId(sessionId);
    try {
      const res = await adminForceFulfilTransaction(sessionId);
      toast.success(res.data?.message || 'Transaction validée manuellement ✅');
      await loadDashboard(true);
      if (activeTab === 'transactions') await loadTransactions(true);
    } catch (e) { toast.error(e?.response?.data?.detail || 'Erreur'); }
    finally { setResyncingId(null); }
  };

  const handleSyncAll = async () => {
    setSyncingAll(true);
    try {
      const res = await adminSyncAllPending(24);
      const { scanned, fulfilled, still_pending, failed, errors } = res.data;
      if (fulfilled > 0) toast.success(`✅ ${fulfilled} paiement(s) récupéré(s) sur ${scanned} scannés !`, { duration: 6000 });
      else if (scanned === 0) toast.info('Aucune transaction en attente à synchroniser');
      else toast.info(`${scanned} scannées · ${still_pending} en attente · ${failed} échouées · ${errors} erreurs`);
      await loadDashboard(true);
      if (activeTab === 'transactions') await loadTransactions(true);
    } catch (e) { toast.error(e?.response?.data?.detail || 'Erreur'); }
    finally { setSyncingAll(false); }
  };

  // Edit payment method on a manual credit transaction
  const handleEditMethod = async (sessionId, newMethod) => {
    if (!sessionId || !newMethod) return;
    try {
      const res = await adminUpdateTransactionMethod(sessionId, newMethod);
      if (res.data?.excluded_from_sales) {
        toast.success('Méthode mise à jour ✓ — exclue des ventes (Offert)');
      } else {
        toast.success('Méthode de paiement mise à jour ✓');
      }
      // Update selectedTxn locally to refresh the dialog
      if (selectedTxn && selectedTxn.session_id === sessionId) {
        setSelectedTxn({
          ...selectedTxn,
          payment_method: newMethod,
          metadata: {
            ...(selectedTxn.metadata || {}),
            payment_method: newMethod,
            payment_method_label: res.data?.transaction?.metadata?.payment_method_label || newMethod,
          },
          excluded_from_sales: res.data?.excluded_from_sales,
        });
      }
      await loadDashboard(true);
      if (activeTab === 'transactions') await loadTransactions(true);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Erreur lors de la modification');
    }
  };

  // ── Sorting + filtering for client-side ─────────────────────
  const sortedTransactions = useMemo(() => {
    const arr = [...transactions];
    arr.sort((a, b) => {
      let va, vb;
      if (sortField === 'created_at') { va = new Date(a.created_at || 0).getTime(); vb = new Date(b.created_at || 0).getTime(); }
      else if (sortField === 'amount') { va = Number(a.amount_gbp || 0); vb = Number(b.amount_gbp || 0); }
      else if (sortField === 'user') { va = (a.user_name || a.user_email || '').toLowerCase(); vb = (b.user_name || b.user_email || '').toLowerCase(); }
      else if (sortField === 'type') { va = a.type || ''; vb = b.type || ''; }
      else if (sortField === 'status') { va = getStatusKey(a); vb = getStatusKey(b); }
      else { va = 0; vb = 0; }
      if (va < vb) return sortDir === 'asc' ? -1 : 1;
      if (va > vb) return sortDir === 'asc' ? 1 : -1;
      return 0;
    });
    return arr;
  }, [transactions, sortField, sortDir]);

  const toggleSort = (field) => {
    if (sortField === field) setSortDir(sortDir === 'asc' ? 'desc' : 'asc');
    else { setSortField(field); setSortDir('desc'); }
  };

  // CSV export
  const exportCSV = () => {
    const headers = ['Date', 'Type', 'Client', 'Email', 'Téléphone', 'Tirage', 'Quantité', 'Montant', 'Statut', 'Mode', 'Session'];
    const rows = sortedTransactions.map((t) => {
      const meta = t.metadata || {};
      const status = STATUS_META[getStatusKey(t)]?.label || '—';
      return [
        fmtDate(t.fulfilled_at || t.created_at),
        getTypeMeta(t).label,
        t.user_name || '',
        t.user_email || '',
        t.user_phone || '',
        t.raffle_title || '',
        meta.quantity || '',
        `£${(t.amount_gbp || 0).toFixed(2)}`,
        status,
        t.gateway_mode || 'direct',
        t.session_id || '',
      ].map((x) => `"${String(x).replace(/"/g, '""')}"`).join(',');
    });
    const csv = [headers.join(','), ...rows].join('\n');
    const blob = new Blob(['\ufeff' + csv], { type: 'text/csv;charset=utf-8' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = `winluxor-ventes-${new Date().toISOString().slice(0, 10)}.csv`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  if (loading || !data) {
    return (
      <div className="min-h-screen flex bg-cream">
        <AdminSidebar />
        <div className="flex-1 flex items-center justify-center">
          <Loader2 className="w-10 h-10 animate-spin text-gold" />
        </div>
      </div>
    );
  }

  const s = data.summary;
  const pendingAlert = (s.pending?.count || 0) > 0;

  return (
    <div className="min-h-screen flex bg-cream">
      <AdminSidebar />
      <main className="flex-1 p-5 lg:p-8 overflow-x-hidden">
        <div className="max-w-[1400px] mx-auto">

          {/* ─── Header ─── */}
          <div className="mb-6 flex items-start justify-between flex-wrap gap-3">
            <div>
              <div className="flex items-center gap-3 mb-1">
                <h1 className="font-display text-3xl font-bold text-rolex-green flex items-center gap-3">
                  <BarChart3 className="w-8 h-8 text-gold-dark" />
                  Ventes & Revenus
                </h1>
                {autoRefresh && (
                  <div className="flex items-center gap-1.5 text-xs text-emerald-700 bg-emerald-50 px-2.5 py-1 rounded-full border border-emerald-200">
                    <span className="relative flex h-2 w-2">
                      <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75" />
                      <span className="relative inline-flex rounded-full h-2 w-2 bg-emerald-500" />
                    </span>
                    Live
                  </div>
                )}
              </div>
              <p className="text-rolex-green/65 text-sm">
                {lastRefresh ? `Dernière mise à jour: ${fmtRelative(lastRefresh)}` : 'Chargement…'} · Auto-refresh 30s · Auto-sync paiements stuck toutes les 5 min
              </p>
            </div>
            <div className="flex items-center gap-2 flex-wrap">
              {pendingAlert && (
                <Button
                  onClick={handleSyncAll}
                  disabled={syncingAll}
                  className="gap-2 bg-amber-500 hover:bg-amber-600 text-white shadow-lg shadow-amber-500/30"
                  data-testid="sales-sync-all-btn"
                >
                  {syncingAll ? <Loader2 className="w-4 h-4 animate-spin" /> : <Zap className="w-4 h-4" />}
                  Récupérer {s.pending.count} stuck
                </Button>
              )}
              <Button variant="outline" onClick={() => setAutoRefresh(!autoRefresh)} className={autoRefresh ? 'border-emerald-300 text-emerald-700' : 'border-stone-300 text-stone-600'}>
                {autoRefresh ? '🟢 Auto' : '⏸️ Pause'}
              </Button>
              <Button variant="outline" onClick={() => { loadDashboard(); if (activeTab === 'transactions') loadTransactions(); if (activeTab === 'by-raffle') loadPerRaffle(); }} disabled={refreshing} className="gap-2">
                {refreshing ? <Loader2 className="w-4 h-4 animate-spin" /> : <RotateCw className="w-4 h-4" />} Rafraîchir
              </Button>
            </div>
          </div>

          {/* ─── Critical alert: stuck payments ─── */}
          {pendingAlert && (
            <Card className="mb-5 border-2 border-amber-300 bg-gradient-to-r from-amber-50 to-orange-50 shadow-md">
              <CardContent className="p-4 flex items-center gap-4 flex-wrap">
                <div className="w-12 h-12 rounded-full bg-amber-100 flex items-center justify-center shrink-0">
                  <AlertCircle className="w-6 h-6 text-amber-600" />
                </div>
                <div className="flex-1 min-w-[260px]">
                  <p className="font-bold text-amber-900 text-base mb-0.5">
                    {s.pending.count} paiement(s) en attente — {fmt(s.pending.amount)}
                  </p>
                  <p className="text-xs text-amber-800/85">
                    Webhook Luxomarket peut-être perdu. Cliquez "Récupérer stuck" pour synchroniser. <span className="italic">L'auto-sync tente déjà toutes les 5 min.</span>
                  </p>
                </div>
                <Button onClick={() => { setActiveTab('transactions'); setFilterStatus('pending'); }} variant="outline" className="border-amber-400 text-amber-800 hover:bg-amber-100 gap-1.5">
                  <Eye className="w-4 h-4" /> Voir
                </Button>
              </CardContent>
            </Card>
          )}

          {/* ─── GLOBAL totals banner (ALWAYS visible, ignores filter) ─── */}
          {data?.global_totals && (
            <Card className="mb-4 bg-gradient-to-r from-rolex-green via-rolex-green-deep to-rolex-green text-cream border-2 border-gold/50 shadow-xl overflow-hidden">
              <CardContent className="p-4 sm:p-5 relative">
                <div className="absolute inset-0 noise-overlay opacity-20" />
                <div className="relative grid grid-cols-1 sm:grid-cols-4 gap-3 sm:gap-4 items-center">
                  <div className="sm:col-span-1">
                    <p className="text-[10px] uppercase tracking-[0.25em] font-bold text-gold mb-1 flex items-center gap-1.5">
                      <Sparkles className="w-3.5 h-3.5" /> Chiffre d'affaires global
                    </p>
                    <p className="text-[11px] text-cream/70 italic leading-snug">
                      Cumul TOUS tirages confondus + recharges. Ignore le filtre.
                    </p>
                  </div>
                  <div data-testid="global-total-revenue">
                    <p className="text-[10px] uppercase tracking-widest text-cream/65 font-bold mb-1">💰 Total</p>
                    <p className="font-mono text-2xl sm:text-3xl font-extrabold text-gold-light leading-none">{fmt(data.global_totals.all_revenue)}</p>
                    <p className="text-[10px] text-cream/55 mt-1">{data.global_totals.all_transactions} transactions</p>
                  </div>
                  <div data-testid="global-ticket-revenue">
                    <p className="text-[10px] uppercase tracking-widest text-emerald-300 font-bold mb-1">🎟️ Tickets</p>
                    <p className="font-mono text-xl sm:text-2xl font-extrabold text-cream leading-none">{fmt(data.global_totals.ticket_revenue)}</p>
                    <p className="text-[10px] text-cream/55 mt-1">{data.global_totals.ticket_transactions} tickets</p>
                  </div>
                  <div data-testid="global-recharge-revenue">
                    <p className="text-[10px] uppercase tracking-widest text-amber-300 font-bold mb-1">💳 Recharges</p>
                    <p className="font-mono text-xl sm:text-2xl font-extrabold text-cream leading-none">{fmt(data.global_totals.recharge_revenue)}</p>
                    <p className="text-[10px] text-cream/55 mt-1">{data.global_totals.recharge_transactions} top-ups</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          )}

          {/* ─── Dashboard-level raffle filter ─── */}
          <Card className="mb-4 bg-gradient-to-r from-rolex-green/5 via-white to-gold/5 border border-gold/30 shadow-sm">
            <CardContent className="p-3 sm:p-4 flex items-center gap-3 flex-wrap">
              <div className="flex items-center gap-2 shrink-0">
                <Filter className="w-4 h-4 text-gold-dark" />
                <Label className="text-sm font-semibold text-rolex-green">Filtrer le dashboard par tirage :</Label>
              </div>
              <div className="flex-1 min-w-[240px]">
                <Select value={dashboardRaffleId} onValueChange={setDashboardRaffleId}>
                  <SelectTrigger data-testid="dashboard-raffle-filter" className="bg-white border-gold/30 max-w-md">
                    <SelectValue placeholder="Tous les tirages (vue globale)" />
                  </SelectTrigger>
                  <SelectContent className="max-h-[400px]">
                    <SelectItem value="all">📊 Tous les tirages (vue globale)</SelectItem>
                    {allRaffles.map((r) => (
                      <SelectItem key={r._id} value={r._id}>
                        🎯 {r.title}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              {/* "Set as default" / "Clear default" buttons */}
              {(() => {
                let storedDefault = 'all';
                try { storedDefault = localStorage.getItem(DASHBOARD_FILTER_STORAGE_KEY) || 'all'; } catch { /* ignore */ }
                const isDefault = storedDefault === dashboardRaffleId;
                return (
                  <div className="flex items-center gap-2 flex-wrap">
                    <Button
                      size="sm"
                      variant="outline"
                      disabled={isDefault}
                      onClick={() => {
                        try {
                          localStorage.setItem(DASHBOARD_FILTER_STORAGE_KEY, dashboardRaffleId);
                          if (dashboardRaffleId === 'all') {
                            toast.success('Vue globale définie par défaut ✓');
                          } else {
                            const r = allRaffles.find((x) => x._id === dashboardRaffleId);
                            toast.success(`📌 « ${r?.title || 'Tirage'} » défini par défaut`);
                          }
                          // Force re-render to refresh disabled state
                          setDashboardRaffleId((v) => v);
                        } catch (e) { toast.error('Impossible de sauvegarder'); }
                      }}
                      className="gap-1.5 border-gold/40 text-rolex-green hover:bg-gold/10"
                      data-testid="dashboard-filter-set-default"
                    >
                      📌 {isDefault ? 'Défaut actuel' : 'Définir par défaut'}
                    </Button>
                    {storedDefault !== 'all' && (
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => {
                          try { localStorage.removeItem(DASHBOARD_FILTER_STORAGE_KEY); } catch { /* ignore */ }
                          setDashboardRaffleId('all');
                          toast.info('Filtre par défaut effacé');
                        }}
                        className="gap-1.5 text-rolex-green/65 hover:text-rolex-green"
                        data-testid="dashboard-filter-clear-default"
                      >
                        ✕ Effacer le défaut
                      </Button>
                    )}
                  </div>
                );
              })()}
              {dashboardRaffleId !== 'all' && (
                <Badge className="bg-gold-gradient text-rolex-green border-none gap-1">
                  <Trophy className="w-3 h-3" />
                  Vue filtrée
                </Badge>
              )}
            </CardContent>
          </Card>

          {/* ─── Revenue split (Tickets vs Wallet recharges) ─── */}
          {dashboardRaffleId !== 'all' && (
            <div className="mb-2 px-1">
              <p className="text-xs text-rolex-green/70 italic flex items-center gap-1.5">
                <Filter className="w-3 h-3" />
                Les 3 cartes ci-dessous sont <strong className="not-italic font-semibold">filtrées sur ce tirage uniquement</strong>. Le CA global reste visible dans le bandeau doré ci-dessus.
              </p>
            </div>
          )}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 mb-5">
            <RevenueSplitCard
              label={dashboardRaffleId === 'all' ? 'Revenus tickets' : 'Revenus tickets (ce tirage)'}
              hint="Ventes directes (Stripe / Crypto / Cash)"
              value={s.ticket_revenue?.revenue || 0}
              count={s.ticket_revenue?.transactions || 0}
              icon={<Ticket className="w-5 h-5 text-emerald-600" />}
              tone="emerald"
              dataTestId="kpi-ticket-revenue"
            />
            <RevenueSplitCard
              label="Recharges crédit wallet"
              hint={dashboardRaffleId !== 'all' ? "Non spécifique au tirage filtré" : "Packs de points + VIP + crédits manuels"}
              value={s.recharge_revenue?.revenue || 0}
              count={s.recharge_revenue?.transactions || 0}
              icon={<Wallet className="w-5 h-5 text-amber-600" />}
              tone="amber"
              disabled={dashboardRaffleId !== 'all'}
              dataTestId="kpi-recharge-revenue"
            />
            <RevenueSplitCard
              label={dashboardRaffleId === 'all' ? 'Total combiné' : 'Total filtré (ce tirage)'}
              hint={dashboardRaffleId === 'all' ? "Tickets + recharges (chiffre d'affaires global)" : "Tickets de ce tirage uniquement"}
              value={(s.ticket_revenue?.revenue || 0) + (dashboardRaffleId !== 'all' ? 0 : (s.recharge_revenue?.revenue || 0))}
              count={(s.ticket_revenue?.transactions || 0) + (dashboardRaffleId !== 'all' ? 0 : (s.recharge_revenue?.transactions || 0))}
              icon={<DollarSign className="w-5 h-5 text-rolex-green" />}
              tone="gold"
              dataTestId="kpi-total-revenue"
            />
          </div>

          {/* ─── KPI grid (4 cards with deltas) ─── */}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-5">
            <KpiCard
              label="Aujourd'hui"
              value={fmt(s.today.revenue)}
              sub={`${s.today.transactions} vente${s.today.transactions !== 1 ? 's' : ''} · ${s.today.new_users} nouveau${s.today.new_users !== 1 ? 'x' : ''} client${s.today.new_users !== 1 ? 's' : ''}`}
              delta={s.today.delta_revenue_pct}
              previous={s.today.previous_revenue}
              previousLabel="vs hier"
              icon={<DollarSign className="w-5 h-5" />}
              tone="emerald"
            />
            <KpiCard
              label="Cette semaine"
              value={fmt(s.week.revenue)}
              sub={`${s.week.transactions} ventes · ${s.week.new_users} nouveaux clients`}
              delta={s.week.delta_revenue_pct}
              previous={s.week.previous_revenue}
              previousLabel="vs sem. dernière"
              icon={<TrendingUp className="w-5 h-5" />}
              tone="gold"
            />
            <KpiCard
              label="Ce mois-ci"
              value={fmt(s.month.revenue)}
              sub={`${s.month.transactions} ventes`}
              delta={s.month.delta_revenue_pct}
              previous={s.month.previous_revenue}
              previousLabel="vs mois dernier"
              icon={<Calendar className="w-5 h-5" />}
              tone="blue"
            />
            <KpiCard
              label="Total cumulé"
              value={fmt(s.all_time.revenue)}
              sub={`${s.all_time.transactions} ventes · panier moyen ${fmt(s.avg_ticket_size)}`}
              icon={<Award className="w-5 h-5" />}
              tone="dark"
            />
          </div>

          {/* ─── Secondary stats: 4 narrow cards ─── */}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-6">
            <MiniStatCard
              label="Taux de conversion"
              value={`${s.conversion_rate}%`}
              icon={<Activity className="w-4 h-4 text-emerald-600" />}
              hint="paiements réussis / tentatives"
            />
            <MiniStatCard
              label="En attente"
              value={s.pending.count}
              valueDetail={fmt(s.pending.amount)}
              icon={<Clock className="w-4 h-4 text-amber-600" />}
              tone={s.pending.count > 0 ? 'amber' : 'neutral'}
              hint="paiements stuck (webhook perdu ?)"
            />
            <MiniStatCard
              label="Échoués"
              value={s.failed.count}
              icon={<XCircle className="w-4 h-4 text-red-600" />}
              tone={s.failed.count > 0 ? 'red' : 'neutral'}
              hint="abandons ou expirations"
            />
            <MiniStatCard
              label="Temps de paiement"
              value={s.avg_fulfilment_seconds < 60 ? `${s.avg_fulfilment_seconds}s` : `${Math.round(s.avg_fulfilment_seconds / 60)}min`}
              icon={<Zap className="w-4 h-4 text-blue-600" />}
              hint="durée moyenne checkout → validé"
            />
          </div>

          {/* ─── Sparkline + payment methods row ─── */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mb-6">
            <Card className="lg:col-span-2 bg-white border border-gold/20">
              <CardHeader className="pb-2">
                <div className="flex items-center justify-between flex-wrap gap-2">
                  <CardTitle className="text-base flex items-center gap-2">
                    <Activity className="w-4 h-4 text-gold-dark" /> Revenu 30 derniers jours
                  </CardTitle>
                  <p className="text-[10px] text-rolex-green/55 font-mono">
                    Total : {fmt((data.sparkline || []).reduce((a, b) => a + b.revenue, 0))}
                  </p>
                </div>
              </CardHeader>
              <CardContent>
                <Sparkline points={data.sparkline || []} />
              </CardContent>
            </Card>

            <Card className="bg-white border border-gold/20">
              <CardHeader className="pb-2">
                <CardTitle className="text-base flex items-center gap-2">
                  <CreditCard className="w-4 h-4 text-gold-dark" /> Modes de paiement
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                <PaymentMethodBar
                  label="Stripe (carte)"
                  value={data.method_distribution?.stripe || 0}
                  total={(data.method_distribution?.stripe || 0) + (data.method_distribution?.credit_only || 0)}
                  icon={<CreditCard className="w-4 h-4 text-emerald-600" />}
                  color="bg-emerald-500"
                />
                <PaymentMethodBar
                  label="Crédit wallet"
                  value={data.method_distribution?.credit_only || 0}
                  total={(data.method_distribution?.stripe || 0) + (data.method_distribution?.credit_only || 0)}
                  icon={<Wallet className="w-4 h-4 text-amber-600" />}
                  color="bg-amber-500"
                />
                <div className="pt-2 border-t border-stone-200">
                  <p className="text-[10px] uppercase tracking-wider text-rolex-green/55 font-bold mb-2">Revenus par type</p>
                  {Object.entries(data.by_type || {}).map(([key, val]) => {
                    const meta = TYPE_META[key] || TYPE_META.point_pack;
                    const Icon = meta.icon;
                    return (
                      <div key={key} className="flex items-center gap-2 text-xs mb-1.5">
                        <Icon className={`w-3 h-3 ${meta.text}`} />
                        <span className="flex-1 text-rolex-green/80">{meta.label}</span>
                        <span className="font-mono font-bold text-rolex-green">{fmtCompact(val.total)}</span>
                        <span className="text-rolex-green/45 text-[10px]">({val.count})</span>
                      </div>
                    );
                  })}
                </div>
              </CardContent>
            </Card>
          </div>

          {/* ─── Tabs ─── */}
          <Tabs value={activeTab} onValueChange={setActiveTab}>
            <TabsList className="mb-5 bg-white border border-gold/30 flex-wrap h-auto">
              <TabsTrigger value="overview" className="gap-2"><BarChart3 className="w-4 h-4" /> Vue d'ensemble</TabsTrigger>
              <TabsTrigger value="transactions" className="gap-2"><ShoppingBag className="w-4 h-4" /> Transactions {totalCount > 0 && <Badge className="bg-gold-dark text-cream border-none text-[10px] ml-1">{totalCount}</Badge>}</TabsTrigger>
              <TabsTrigger value="by-raffle" className="gap-2"><Trophy className="w-4 h-4 text-gold-dark" /> Par tirage</TabsTrigger>
              <TabsTrigger value="customers" className="gap-2"><UsersIcon className="w-4 h-4" /> Top clients</TabsTrigger>
            </TabsList>

            {/* ─── Overview tab ─── */}
            <TabsContent value="overview">
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                {/* Top raffles */}
                {data.top_raffles?.length > 0 && (
                  <Card className="border border-gold/20 bg-white">
                    <CardHeader className="pb-2">
                      <CardTitle className="text-base flex items-center gap-2"><Trophy className="w-4 h-4 text-gold-dark" /> Top tirages</CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-2">
                      {data.top_raffles.map((r, i) => (
                        <div key={r._id} className="flex items-center gap-3 p-2 rounded-lg hover:bg-cream/60 transition-colors">
                          <div className={`w-8 h-8 rounded-full ${i === 0 ? 'bg-gold-gradient text-rolex-green' : 'bg-stone-200 text-stone-700'} flex items-center justify-center font-bold text-xs`}>{i + 1}</div>
                          <div className="flex-1 min-w-0">
                            <p className="font-semibold text-rolex-green text-sm truncate">{r.title}</p>
                            <p className="text-[10px] text-rolex-green/55">{r.sold_tickets.toLocaleString()} / {r.max_tickets.toLocaleString()} tickets · {fmt(r.ticket_cost)} l'unité</p>
                          </div>
                          <div className="text-right">
                            <p className="font-mono font-bold text-emerald-700 text-sm">{fmtCompact(r.sold_tickets * r.ticket_cost)}</p>
                            <p className="text-[9px] text-rolex-green/55">estimé</p>
                          </div>
                        </div>
                      ))}
                    </CardContent>
                  </Card>
                )}

                {/* Recent activity feed */}
                <Card className="border border-gold/20 bg-white">
                  <CardHeader className="pb-2">
                    <CardTitle className="text-base flex items-center gap-2"><Activity className="w-4 h-4 text-gold-dark" /> Activité récente</CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-2 max-h-[400px] overflow-y-auto">
                    {(data.recent || []).slice(0, 12).map((t, i) => {
                      const meta = t.metadata || {};
                      const statusKey = getStatusKey(t);
                      const statusMeta = STATUS_META[statusKey];
                      const typeMeta = getTypeMeta(t);
                      const Icon = typeMeta.icon;
                      return (
                        <button
                          type="button"
                          key={`${t._id}-${i}`}
                          onClick={() => setSelectedTxn(t)}
                          className="w-full flex items-center gap-3 p-2.5 rounded-lg hover:bg-cream/60 transition-colors text-left border border-transparent hover:border-gold/30"
                        >
                          <div className={`w-9 h-9 rounded-lg flex items-center justify-center ${typeMeta.bg}`}>
                            <Icon className={`w-4 h-4 ${typeMeta.text}`} />
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2">
                              <p className="font-semibold text-xs text-rolex-green truncate">{t.user_name || t.user_email || 'Anonyme'}</p>
                              <Badge className={`${statusMeta.cls} text-white border-none text-[9px] px-1.5 py-0`}>{statusMeta.label}</Badge>
                            </div>
                            <p className="text-[10px] text-rolex-green/65 truncate">
                              {typeMeta.label}
                              {t.raffle_title && ` · ${t.raffle_title}`}
                              {meta.quantity && ` · x${meta.quantity}`}
                              {' · '}{fmtRelative(t.fulfilled_at || t.created_at)}
                            </p>
                          </div>
                          <div className="text-right shrink-0">
                            <p className={`font-mono font-bold text-sm ${t.amount_gbp > 0 ? 'text-emerald-700' : 'text-amber-700'}`}>
                              {t.amount_gbp > 0 ? fmt(t.amount_gbp) : `£${t.credit_used || 0}`}
                            </p>
                            <p className="text-[9px] text-rolex-green/45 uppercase">{paymentMethodLabel(t)}</p>
                          </div>
                        </button>
                      );
                    })}
                  </CardContent>
                </Card>
              </div>
            </TabsContent>

            {/* ─── Transactions tab ─── */}
            <TabsContent value="transactions">
              {/* Filters */}
              <Card className="mb-3 border border-gold/20 bg-white">
                <CardContent className="p-3 flex items-center gap-2 flex-wrap">
                  <div className="flex-1 min-w-[220px] relative">
                    <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-rolex-green/45" />
                    <Input
                      placeholder="Rechercher par email client…"
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      className="pl-9 border-gold/30 h-9"
                      data-testid="sales-search-email"
                    />
                  </div>
                  <select value={filterStatus} onChange={(e) => setFilterStatus(e.target.value)} className="p-2 border border-gold/30 rounded-md bg-white text-xs h-9 cursor-pointer">
                    <option value="all">Tous les statuts</option>
                    <option value="paid">✓ Payés</option>
                    <option value="pending">⏳ En attente</option>
                    <option value="failed">❌ Échoués</option>
                  </select>
                  <select value={filterType} onChange={(e) => setFilterType(e.target.value)} className="p-2 border border-gold/30 rounded-md bg-white text-xs h-9 cursor-pointer" data-testid="sales-filter-type">
                    <option value="all">Tous les types</option>
                    <option value="point_pack">💰 Recharge</option>
                    <option value="vip">👑 VIP</option>
                    <option value="raffle_direct">🎟️ Tickets carte</option>
                    <option value="raffle_credit">⭐ Tickets crédit</option>
                    <option value="manual_credit">✨ Vente manuelle</option>
                  </select>
                  <select value={filterRaffleId} onChange={(e) => setFilterRaffleId(e.target.value)} className="p-2 border border-gold/30 rounded-md bg-white text-xs h-9 max-w-[200px] cursor-pointer" data-testid="sales-filter-raffle">
                    <option value="all">🏆 Tous les tirages</option>
                    {allRaffles.map((r) => (<option key={r._id} value={r._id}>{r.title}</option>))}
                  </select>
                  {(filterStatus !== 'all' || filterType !== 'all' || filterRaffleId !== 'all' || searchTerm) && (
                    <Button variant="ghost" size="sm" onClick={() => { setFilterStatus('all'); setFilterType('all'); setFilterRaffleId('all'); setSearchTerm(''); }} className="text-rolex-green/65 hover:text-rolex-green h-9">✕ Effacer</Button>
                  )}
                  <Button variant="outline" onClick={exportCSV} className="gap-2 border-emerald-300 text-emerald-700 hover:bg-emerald-50 h-9 ml-auto">
                    <Download className="w-4 h-4" /> CSV
                  </Button>
                </CardContent>
              </Card>

              {/* Active raffle filter banner */}
              {filterRaffleId !== 'all' && (() => {
                const r = allRaffles.find((x) => x._id === filterRaffleId);
                if (!r) return null;
                return (
                  <Card className="mb-3 border-2 border-gold/40 bg-gold/5">
                    <CardContent className="p-3 flex items-center gap-3 flex-wrap">
                      {(r.images?.[0] || r.image_url) && (
                        <img src={r.images?.[0] || r.image_url} alt={r.title} className="w-12 h-12 object-cover rounded-lg border border-gold/30" />
                      )}
                      <div className="flex-1 min-w-0">
                        <p className="text-[9px] uppercase tracking-wider text-gold-dark font-bold">Filtré sur le tirage</p>
                        <p className="font-display text-base text-rolex-green font-bold truncate">{r.title}</p>
                      </div>
                      <Button variant="outline" size="sm" onClick={() => setFilterRaffleId('all')} className="border-gold/40 text-gold-dark hover:bg-gold/20">✕</Button>
                    </CardContent>
                  </Card>
                );
              })()}

              {/* Transactions table */}
              <Card className="border border-gold/20 bg-white">
                <CardContent className="p-0">
                  {sortedTransactions.length === 0 ? (
                    <div className="p-16 text-center text-rolex-green/55">
                      <ShoppingBag className="w-12 h-12 mx-auto mb-3 opacity-30" />
                      <p className="font-semibold">Aucune transaction ne correspond</p>
                      <p className="text-xs mt-1">Essayez d'élargir les filtres ou de rafraîchir</p>
                    </div>
                  ) : (
                    <div className="overflow-x-auto">
                      <table className="w-full text-sm">
                        <thead className="bg-cream/80 border-b border-gold/20 sticky top-0 z-10">
                          <tr>
                            <Th label="Date" field="created_at" sortField={sortField} sortDir={sortDir} toggleSort={toggleSort} />
                            <Th label="Type" field="type" sortField={sortField} sortDir={sortDir} toggleSort={toggleSort} />
                            <Th label="Client" field="user" sortField={sortField} sortDir={sortDir} toggleSort={toggleSort} />
                            <th className="text-left px-3 py-2 text-[10px] uppercase tracking-wider text-rolex-green/70 font-bold">Détail</th>
                            <Th label="Montant" field="amount" sortField={sortField} sortDir={sortDir} toggleSort={toggleSort} align="right" />
                            <Th label="Statut" field="status" sortField={sortField} sortDir={sortDir} toggleSort={toggleSort} align="center" />
                            <th className="text-center px-3 py-2 text-[10px] uppercase tracking-wider text-rolex-green/70 font-bold">Actions</th>
                          </tr>
                        </thead>
                        <tbody>
                          {sortedTransactions.map((t, i) => (
                            <TransactionRow
                              key={`${t._id || t.session_id}-${i}`}
                              t={t}
                              onSelect={() => setSelectedTxn(t)}
                              onViewUser={() => handleViewUser(t.user_id)}
                              onResync={() => handleResync(t.session_id)}
                              onForceFulfil={() => handleForceFulfil(t.session_id)}
                              busy={resyncingId === t.session_id}
                            />
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </CardContent>
              </Card>
            </TabsContent>

            {/* ─── Par tirage tab ─── */}
            <TabsContent value="by-raffle">
              {loadingPerRaffle || !perRaffle ? (
                <div className="flex items-center justify-center py-20"><Loader2 className="w-8 h-8 animate-spin text-gold" /></div>
              ) : (
                <>
                  <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-4">
                    <MiniStatCard label="Tirages actifs" value={perRaffle.summary.raffles_count} icon={<Trophy className="w-4 h-4 text-gold-dark" />} />
                    <MiniStatCard label="Revenu total" value={fmt(perRaffle.summary.total_revenue)} icon={<DollarSign className="w-4 h-4 text-emerald-600" />} />
                    <MiniStatCard label="Tickets vendus" value={perRaffle.summary.total_tickets_sold.toLocaleString()} icon={<Ticket className="w-4 h-4 text-blue-600" />} />
                    <MiniStatCard label="En attente" value={fmt(perRaffle.summary.total_pending_amount)} icon={<Clock className="w-4 h-4 text-amber-600" />} tone={perRaffle.summary.total_pending_amount > 0 ? 'amber' : 'neutral'} />
                  </div>

                  <Card className="border border-gold/20 bg-white">
                    <CardContent className="p-0">
                      {perRaffle.raffles.length === 0 ? (
                        <div className="p-12 text-center text-rolex-green/55">
                          <Trophy className="w-10 h-10 mx-auto mb-3 opacity-40" />
                          <p>Aucun tirage trouvé</p>
                        </div>
                      ) : (
                        <div className="divide-y divide-gold/10">
                          {perRaffle.raffles.map((r) => (
                            <PerRaffleRow key={r._id} r={r} onView={() => setSelectedRaffle(r)} onFilter={() => { setFilterRaffleId(r._id); setActiveTab('transactions'); }} />
                          ))}
                        </div>
                      )}
                    </CardContent>
                  </Card>
                </>
              )}
            </TabsContent>

            {/* ─── Top customers tab ─── */}
            <TabsContent value="customers">
              <Card className="border border-gold/20 bg-white">
                <CardHeader>
                  <CardTitle className="text-base flex items-center gap-2"><UsersIcon className="w-4 h-4 text-gold-dark" /> Meilleurs clients</CardTitle>
                  <p className="text-xs text-rolex-green/55">Top 5 par dépense cumulée. Cliquez pour voir l'historique complet.</p>
                </CardHeader>
                <CardContent className="p-0">
                  {(!data.top_customers || data.top_customers.length === 0) ? (
                    <div className="p-12 text-center text-rolex-green/55">
                      <UsersIcon className="w-10 h-10 mx-auto mb-3 opacity-40" />
                      <p>Aucun client n'a encore acheté</p>
                    </div>
                  ) : (
                    <div className="divide-y divide-gold/10">
                      {data.top_customers.map((c, i) => (
                        <button
                          key={c._id}
                          type="button"
                          onClick={() => handleViewUser(c._id)}
                          className="w-full flex items-center gap-4 p-4 hover:bg-cream/50 transition-colors text-left"
                        >
                          <div className={`w-12 h-12 rounded-full ${userColor(c.email)} text-white flex items-center justify-center font-bold text-base shadow-md`}>{initials(c.name, c.email)}</div>
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2 flex-wrap mb-0.5">
                              <p className="font-bold text-rolex-green">{c.name}</p>
                              {c.vip_status && <Badge className="bg-gold-gradient text-rolex-green border-none text-[9px] px-1.5 py-0">VIP</Badge>}
                              <Badge variant="outline" className="text-[9px]">#{i + 1}</Badge>
                            </div>
                            <p className="text-xs text-rolex-green/65 flex items-center gap-3 flex-wrap">
                              <span className="flex items-center gap-1"><Mail className="w-3 h-3" /> {c.email}</span>
                              {c.phone && <span className="flex items-center gap-1"><Phone className="w-3 h-3" /> {c.phone}</span>}
                            </p>
                          </div>
                          <div className="text-right">
                            <p className="font-mono text-xl font-extrabold text-emerald-700">{fmt(c.total_spent)}</p>
                            <p className="text-[10px] text-rolex-green/55">{c.transaction_count} transaction{c.transaction_count !== 1 ? 's' : ''}</p>
                          </div>
                        </button>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>
            </TabsContent>
          </Tabs>
        </div>

        {/* Dialogs */}
        {selectedTxn && <TxnDetailDialog txn={selectedTxn} onClose={() => setSelectedTxn(null)} onViewUser={handleViewUser} onResync={handleResync} onForceFulfil={handleForceFulfil} onEditMethod={handleEditMethod} resyncingId={resyncingId} />}
        {userDrillDown && <UserDrillDown data={userDrillDown} onClose={() => setUserDrillDown(null)} onSelectTxn={(t) => { setSelectedTxn(t); setUserDrillDown(null); }} />}
        {selectedRaffle && <RaffleDetailDialog raffle={selectedRaffle} onClose={() => setSelectedRaffle(null)} onFilter={() => { setFilterRaffleId(selectedRaffle._id); setActiveTab('transactions'); setSelectedRaffle(null); }} />}
      </main>
    </div>
  );
}

// ─── Sub components ──────────────────────────────────────────

function KpiCard({ label, value, sub, delta, previous, previousLabel, icon, tone = 'emerald' }) {
  const tones = {
    emerald: { card: 'bg-emerald-50 border-emerald-200', iconBg: 'bg-emerald-500 text-white' },
    gold:    { card: 'bg-gold/10 border-gold/40',        iconBg: 'bg-gold-gradient text-rolex-green' },
    blue:    { card: 'bg-blue-50 border-blue-200',       iconBg: 'bg-blue-500 text-white' },
    dark:    { card: 'bg-rolex-green text-cream border-rolex-green', iconBg: 'bg-gold-gradient text-rolex-green' },
  };
  const t = tones[tone] || tones.emerald;
  const isDark = tone === 'dark';
  const deltaPositive = typeof delta === 'number' && delta >= 0;
  return (
    <Card className={`border-2 shadow-sm ${t.card} transition-shadow hover:shadow-md`}>
      <CardContent className="p-4">
        <div className="flex items-start justify-between mb-2">
          <p className={`text-[10px] uppercase tracking-wider font-bold ${isDark ? 'text-gold' : 'text-rolex-green/70'}`}>{label}</p>
          <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${t.iconBg}`}>{icon}</div>
        </div>
        <p className={`font-mono text-2xl lg:text-3xl font-extrabold ${isDark ? 'text-gold-gradient' : 'text-rolex-green'}`}>{value}</p>
        <p className={`text-[11px] mt-1 ${isDark ? 'text-cream/65' : 'text-rolex-green/65'}`}>{sub}</p>
        {typeof delta === 'number' && (
          <div className="mt-2 flex items-center gap-2">
            <Badge className={`text-[10px] gap-1 border-none ${deltaPositive ? 'bg-emerald-500/15 text-emerald-700' : 'bg-red-500/15 text-red-700'}`}>
              {deltaPositive ? <ArrowUpRight className="w-3 h-3" /> : <ArrowDownRight className="w-3 h-3" />}
              {deltaPositive ? '+' : ''}{delta}%
            </Badge>
            <span className={`text-[10px] ${isDark ? 'text-cream/55' : 'text-rolex-green/55'}`}>
              {previousLabel}: {fmt(previous || 0)}
            </span>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

function RevenueSplitCard({ label, hint, value, count, icon, tone = 'emerald', disabled = false, dataTestId }) {
  const tones = {
    emerald: { card: 'bg-gradient-to-br from-emerald-50 to-emerald-100/60 border-emerald-300', iconBg: 'bg-emerald-500 text-white', accent: 'text-emerald-700' },
    amber:   { card: 'bg-gradient-to-br from-amber-50 to-amber-100/60 border-amber-300',       iconBg: 'bg-amber-500 text-white',   accent: 'text-amber-700' },
    gold:    { card: 'bg-gradient-to-br from-gold/10 to-gold/20 border-gold/50',               iconBg: 'bg-gold-gradient text-rolex-green', accent: 'text-rolex-green' },
  };
  const t = tones[tone] || tones.emerald;
  return (
    <Card className={`border-2 shadow-sm transition-all ${t.card} ${disabled ? 'opacity-50' : 'hover:shadow-md'}`} data-testid={dataTestId}>
      <CardContent className="p-4">
        <div className="flex items-start justify-between mb-2">
          <div>
            <p className="text-[10px] uppercase tracking-wider font-bold text-rolex-green/70">{label}</p>
            {hint && <p className="text-[10px] text-rolex-green/55 mt-0.5 italic">{hint}</p>}
          </div>
          <div className={`w-9 h-9 rounded-lg flex items-center justify-center shrink-0 ${t.iconBg}`}>{icon}</div>
        </div>
        <p className={`font-mono text-2xl lg:text-3xl font-extrabold mt-2 ${t.accent}`}>{fmt(value)}</p>
        <p className="text-[11px] mt-1 text-rolex-green/65">
          {count} transaction{count !== 1 ? 's' : ''}
          {disabled && <span className="ml-1 italic">— non applicable au filtre tirage</span>}
        </p>
      </CardContent>
    </Card>
  );
}


function MiniStatCard({ label, value, valueDetail, icon, tone = 'neutral', hint }) {
  const tones = {
    neutral: 'border-stone-200 bg-white',
    amber:   'border-amber-300 bg-amber-50',
    red:     'border-red-300 bg-red-50',
    emerald: 'border-emerald-300 bg-emerald-50',
  };
  return (
    <Card className={`border ${tones[tone] || tones.neutral} shadow-sm`}>
      <CardContent className="p-3">
        <div className="flex items-center justify-between mb-0.5">
          <p className="text-[10px] uppercase tracking-wider font-bold text-rolex-green/65">{label}</p>
          {icon}
        </div>
        <p className="font-mono text-xl font-bold text-rolex-green">{value}</p>
        {valueDetail && <p className="text-[10px] text-rolex-green/65 font-mono">{valueDetail}</p>}
        {hint && <p className="text-[10px] text-rolex-green/55 mt-0.5 italic">{hint}</p>}
      </CardContent>
    </Card>
  );
}

function Sparkline({ points = [] }) {
  if (!points.length) {
    return <p className="text-xs text-rolex-green/45 italic">Aucune donnée</p>;
  }
  const max = Math.max(1, ...points.map((p) => p.revenue || 0));
  const W = 600;
  const H = 100;
  const stepX = W / Math.max(1, points.length - 1);
  const path = points.map((p, i) => {
    const x = i * stepX;
    const y = H - ((p.revenue || 0) / max) * (H - 6) - 3;
    return `${i === 0 ? 'M' : 'L'} ${x.toFixed(1)},${y.toFixed(1)}`;
  }).join(' ');
  const areaPath = `${path} L ${W},${H} L 0,${H} Z`;
  const max_pt = points.reduce((m, p) => (p.revenue > (m?.revenue || 0) ? p : m), null);
  return (
    <div>
      <svg viewBox={`0 0 ${W} ${H}`} preserveAspectRatio="none" className="w-full h-24">
        <defs>
          <linearGradient id="sl-grad" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor="hsl(var(--gold))" stopOpacity="0.4" />
            <stop offset="100%" stopColor="hsl(var(--gold))" stopOpacity="0" />
          </linearGradient>
        </defs>
        <path d={areaPath} fill="url(#sl-grad)" />
        <path d={path} fill="none" stroke="hsl(var(--gold-dark))" strokeWidth="1.5" />
        {points.map((p, i) => {
          if (i % 6 !== 0 && i !== points.length - 1) return null;
          const x = i * stepX;
          const y = H - ((p.revenue || 0) / max) * (H - 6) - 3;
          return <circle key={i} cx={x} cy={y} r="2" fill="hsl(var(--gold-dark))" />;
        })}
      </svg>
      <div className="flex justify-between text-[9px] text-rolex-green/55 mt-1 font-mono">
        <span>{points[0]?.date}</span>
        {max_pt && <span className="text-gold-dark font-bold">Pic: {fmtCompact(max_pt.revenue)} ({max_pt.date})</span>}
        <span>{points[points.length - 1]?.date}</span>
      </div>
    </div>
  );
}

function PaymentMethodBar({ label, value, total, icon, color }) {
  const pct = total > 0 ? Math.round((value / total) * 100) : 0;
  return (
    <div>
      <div className="flex items-center justify-between mb-1">
        <span className="text-xs font-semibold text-rolex-green flex items-center gap-1.5">{icon} {label}</span>
        <span className="font-mono text-xs text-rolex-green/65">{value} ({pct}%)</span>
      </div>
      <div className="h-1.5 bg-stone-100 rounded-full overflow-hidden">
        <div className={`h-full ${color} transition-all duration-500`} style={{ width: `${pct}%` }} />
      </div>
    </div>
  );
}

function Th({ label, field, sortField, sortDir, toggleSort, align = 'left' }) {
  const active = sortField === field;
  return (
    <th className={`text-${align} px-3 py-2 text-[10px] uppercase tracking-wider text-rolex-green/70 font-bold`}>
      <button
        type="button"
        onClick={() => toggleSort(field)}
        className={`inline-flex items-center gap-1 hover:text-rolex-green transition-colors ${active ? 'text-rolex-green' : ''}`}
      >
        {label}
        {active && (sortDir === 'asc' ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />)}
      </button>
    </th>
  );
}

function TransactionRow({ t, onSelect, onViewUser, onResync, onForceFulfil, busy }) {
  const meta = t.metadata || {};
  const statusKey = getStatusKey(t);
  const statusMeta = STATUS_META[statusKey];
  const typeMeta = getTypeMeta(t);
  const Icon = typeMeta.icon;
  const isUnresolved = !t.fulfilled && t.status !== 'expired' && t.status !== 'failed' && t.status !== 'cancelled';
  const isToday = (() => {
    if (!t.created_at) return false;
    const d = new Date(t.created_at);
    const today = new Date();
    return d.getDate() === today.getDate() && d.getMonth() === today.getMonth() && d.getFullYear() === today.getFullYear();
  })();

  let detail = '';
  if (t.type === 'point_pack') detail = `+£${meta.points_to_credit || '?'} crédit`;
  else if (t.type === 'vip') detail = meta.plan_name || 'VIP';
  else if (t.type === 'raffle_direct' || t.type === 'raffle_credit') detail = `${t.raffle_title || '?'} ${meta.quantity ? `× ${meta.quantity}` : ''}`;

  return (
    <tr className={`border-b border-gold/10 hover:bg-cream/40 cursor-pointer transition-colors ${isToday ? 'bg-emerald-50/30' : ''} ${isUnresolved ? 'bg-amber-50/40' : ''}`} onClick={onSelect}>
      <td className="px-3 py-2.5 text-xs font-mono whitespace-nowrap">
        <p className="text-rolex-green/85">{fmtRelative(t.fulfilled_at || t.created_at)}</p>
        <p className="text-[9px] text-rolex-green/45">{fmtDate(t.fulfilled_at || t.created_at)}</p>
      </td>
      <td className="px-3 py-2.5">
        <div className={`inline-flex items-center gap-1.5 px-2 py-1 rounded-md ${typeMeta.bg} ${typeMeta.text} text-[10px] font-semibold`}>
          <Icon className="w-3 h-3" /> {typeMeta.label}
        </div>
      </td>
      <td className="px-3 py-2.5">
        <button type="button" onClick={(e) => { e.stopPropagation(); onViewUser(); }} className="flex items-center gap-2 text-left hover:opacity-80 transition-opacity">
          <div className={`w-8 h-8 rounded-full ${userColor(t.user_email)} text-white flex items-center justify-center text-[10px] font-bold shrink-0`}>{initials(t.user_name, t.user_email)}</div>
          <div className="min-w-0">
            <p className="font-semibold text-rolex-green text-xs truncate max-w-[140px]">{t.user_name || 'Anonyme'}</p>
            <p className="text-[9px] text-rolex-green/55 truncate max-w-[140px]">{t.user_email || '—'}</p>
          </div>
        </button>
      </td>
      <td className="px-3 py-2.5 text-xs">
        <p className="text-rolex-green/90 truncate max-w-[200px]" title={detail}>{detail}</p>
        {t.ticket_numbers?.length > 0 && (
          <p className="text-[9px] text-gold-dark font-mono truncate max-w-[200px]">🎟️ {t.ticket_numbers.slice(0, 3).join(', ')}{t.ticket_numbers.length > 3 ? '…' : ''}</p>
        )}
      </td>
      <td className="px-3 py-2.5 text-right">
        {t.amount_gbp > 0 ? (
          <p className="font-mono font-bold text-emerald-700 text-base">{fmt(t.amount_gbp)}</p>
        ) : (
          <p className="font-mono font-bold text-amber-700 text-sm">£{Number(t.credit_used || 0).toFixed(0)} <span className="text-[9px] opacity-65 font-normal">crédit</span></p>
        )}
        {t.credit_used > 0 && t.amount_gbp > 0 && (
          <p className="text-[9px] text-amber-700">+ £{t.credit_used} crédit</p>
        )}
      </td>
      <td className="px-3 py-2.5 text-center">
        <Badge className={`text-[10px] ${statusMeta.cls} text-white border-none`}>{statusMeta.label}</Badge>
        {t.gateway_mode === 'external' && <p className="text-[9px] text-rolex-green/45 mt-0.5">via Luxomarket</p>}
        {t.gateway_mode === 'nowpayments' && <p className="text-[9px] text-orange-600 mt-0.5 font-bold">🪙 Crypto · {(t.pay_currency || '').toUpperCase()}</p>}
        {t.gateway_mode === 'manual' && <p className="text-[9px] text-purple-600 mt-0.5 font-bold">{paymentMethodLabel(t).toUpperCase()}</p>}
      </td>
      <td className="px-3 py-2.5 text-center">
        <div className="flex items-center justify-center gap-1">
          <Button size="sm" variant="ghost" onClick={(e) => { e.stopPropagation(); onSelect(); }} className="h-7 w-7 p-0" title="Voir détails">
            <Eye className="w-3.5 h-3.5 text-rolex-green/70" />
          </Button>
          {isUnresolved && (
            <>
              <Button
                size="sm"
                variant="ghost"
                disabled={busy}
                onClick={(e) => { e.stopPropagation(); onResync(); }}
                className="h-7 w-7 p-0"
                title="Re-vérifier le paiement (Luxomarket)"
                data-testid="row-resync-btn"
              >
                {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin text-emerald-600" /> : <RefreshCw className="w-3.5 h-3.5 text-emerald-600" />}
              </Button>
              <Button
                size="sm"
                variant="ghost"
                disabled={busy}
                onClick={(e) => { e.stopPropagation(); onForceFulfil(); }}
                className="h-7 w-7 p-0"
                title="Forcer la validation"
                data-testid="row-force-fulfil-btn"
              >
                <ShieldCheck className="w-3.5 h-3.5 text-amber-600" />
              </Button>
            </>
          )}
        </div>
      </td>
    </tr>
  );
}

function TxnDetailDialog({ txn, onClose, onViewUser, onResync, onForceFulfil, onEditMethod, resyncingId }) {
  const meta = txn.metadata || {};
  const statusKey = getStatusKey(txn);
  const statusMeta = STATUS_META[statusKey];
  const StatusIcon = statusMeta.icon;
  const isUnresolved = !txn.fulfilled && txn.status !== 'expired' && txn.status !== 'failed' && txn.status !== 'cancelled';
  const isExternal = (txn.gateway_mode || 'direct') === 'external';
  const isManual = (txn.gateway_mode || 'direct') === 'manual';
  const busy = resyncingId === txn.session_id;
  const typeMeta = getTypeMeta(txn);
  // Local state for method editing UX
  const currentMethod = (meta.payment_method || txn.payment_method || 'offert').toLowerCase();
  const [editingMethod, setEditingMethod] = useState(false);
  const [newMethod, setNewMethod] = useState(currentMethod);
  const [savingMethod, setSavingMethod] = useState(false);
  const handleSaveMethod = async () => {
    if (newMethod === currentMethod) {
      setEditingMethod(false);
      return;
    }
    setSavingMethod(true);
    try {
      await onEditMethod?.(txn.session_id, newMethod);
      setEditingMethod(false);
    } finally {
      setSavingMethod(false);
    }
  };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-2xl max-h-[88vh] overflow-y-auto bg-cream">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <FileText className="w-5 h-5 text-gold-dark" /> Détails de la transaction
          </DialogTitle>
          <DialogDescription className="font-mono text-[10px] break-all">
            Session : <code className="bg-white px-1.5 py-0.5 rounded">{txn.session_id}</code>
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          {/* Status banner */}
          <div className={`p-4 rounded-xl text-center ${statusMeta.cls} text-white shadow-md`}>
            <div className="flex items-center justify-center gap-2 mb-1">
              <StatusIcon className="w-6 h-6" />
              <p className="text-2xl font-extrabold uppercase tracking-wider">{statusMeta.label}</p>
            </div>
            <p className="font-mono text-3xl mt-1">{fmt(txn.amount_gbp)}</p>
            {txn.credit_used > 0 && <p className="text-xs mt-1 opacity-90">+ £{txn.credit_used} crédit utilisé</p>}
          </div>

          {/* Recovery panel */}
          {isUnresolved && (
            <div className="rounded-xl border-2 border-amber-300 bg-amber-50 p-4">
              <p className="text-sm font-bold text-amber-900 mb-2 flex items-center gap-2">
                <AlertCircle className="w-4 h-4" /> Transaction non finalisée
              </p>
              <p className="text-xs text-amber-800 mb-3">
                Si le client a payé mais que le webhook a été perdu, cliquez "Re-vérifier" pour interroger {isExternal ? 'Luxomarket' : 'Stripe'} et finaliser automatiquement.
              </p>
              <div className="flex flex-wrap gap-2">
                <Button onClick={() => onResync?.(txn.session_id)} disabled={busy} className="bg-emerald-600 text-white hover:bg-emerald-700 gap-2" data-testid="txn-resync-btn">
                  {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}
                  Re-vérifier le paiement
                </Button>
                <Button onClick={() => onForceFulfil?.(txn.session_id)} disabled={busy} variant="outline" className="border-amber-400 text-amber-800 hover:bg-amber-100 gap-2" data-testid="txn-force-fulfil-btn">
                  <ShieldCheck className="w-4 h-4" /> Forcer la validation
                </Button>
              </div>
              <p className="text-[10px] text-amber-700 mt-2 italic">
                💡 "Forcer" est à utiliser UNIQUEMENT si vous avez vérifié manuellement le paiement (Stripe Dashboard).
              </p>
            </div>
          )}

          {/* Editable payment method (manual transactions only) */}
          {isManual && (
            <div className="rounded-xl border-2 border-purple-200 bg-purple-50/60 p-4" data-testid="txn-method-editor">
              <div className="flex items-center justify-between mb-2 flex-wrap gap-2">
                <p className="text-sm font-bold text-purple-900 flex items-center gap-2">
                  <Edit3 className="w-4 h-4" /> Méthode de paiement
                </p>
                {!editingMethod && (
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => { setNewMethod(currentMethod); setEditingMethod(true); }}
                    className="h-8 border-purple-300 text-purple-700 hover:bg-purple-100 gap-1.5"
                    data-testid="txn-method-edit-btn"
                  >
                    <Edit3 className="w-3.5 h-3.5" /> Modifier
                  </Button>
                )}
              </div>
              {!editingMethod ? (
                <div className="flex items-center gap-2 flex-wrap">
                  <Badge className="bg-purple-100 text-purple-700 border-purple-200 text-sm py-1 px-3" data-testid="txn-current-method">
                    {meta.payment_method_label || methodLabelFr(currentMethod)}
                  </Badge>
                  {(currentMethod === 'offert' || currentMethod === 'gift' || txn.excluded_from_sales) && (
                    <Badge variant="outline" className="border-amber-400 text-amber-700 gap-1">
                      <AlertCircle className="w-3 h-3" /> Exclu des ventes
                    </Badge>
                  )}
                </div>
              ) : (
                <div className="flex items-center gap-2 flex-wrap">
                  <div className="flex-1 min-w-[180px]">
                    <Select value={newMethod} onValueChange={setNewMethod}>
                      <SelectTrigger className="bg-white border-purple-300" data-testid="txn-method-select">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="cash">💵 Espèces</SelectItem>
                        <SelectItem value="bank_transfer">🏦 Virement bancaire</SelectItem>
                        <SelectItem value="card">💳 Carte bancaire</SelectItem>
                        <SelectItem value="crypto">₿ Crypto</SelectItem>
                        <SelectItem value="other">🔧 Autre</SelectItem>
                        <SelectItem value="offert">🎁 Offert (exclu des ventes)</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <Button
                    size="sm"
                    onClick={handleSaveMethod}
                    disabled={savingMethod}
                    className="bg-purple-600 hover:bg-purple-700 text-white gap-1.5"
                    data-testid="txn-method-save-btn"
                  >
                    {savingMethod ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Save className="w-3.5 h-3.5" />}
                    Enregistrer
                  </Button>
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => setEditingMethod(false)}
                    disabled={savingMethod}
                    className="text-purple-700"
                    data-testid="txn-method-cancel-btn"
                  >
                    Annuler
                  </Button>
                </div>
              )}
              <p className="text-[10px] text-purple-700/80 mt-2 italic">
                💡 Passer en "Offert" retire cette transaction du total des ventes. Choisir une vraie méthode la réintègre.
              </p>
            </div>
          )}

          {/* Client */}
          <Section title="Client">
            <button type="button" onClick={() => onViewUser(txn.user_id)} className="w-full flex items-center gap-3 p-3 rounded-lg bg-white border border-gold/20 hover:border-gold/50 transition-colors text-left">
              <div className={`w-12 h-12 rounded-full ${userColor(txn.user_email)} text-white flex items-center justify-center font-bold text-base shadow-md shrink-0`}>{initials(txn.user_name, txn.user_email)}</div>
              <div className="flex-1 min-w-0">
                <p className="font-bold text-rolex-green">{txn.user_name || 'Anonyme'}</p>
                <p className="text-xs text-rolex-green/65 flex items-center gap-1.5 truncate"><Mail className="w-3 h-3" /> {txn.user_email}</p>
                {txn.user_phone && <p className="text-xs text-rolex-green/55 flex items-center gap-1.5"><Phone className="w-3 h-3" /> {txn.user_phone}</p>}
              </div>
              <Eye className="w-4 h-4 text-rolex-green/45 shrink-0" />
            </button>
          </Section>

          {/* Transaction info */}
          <Section title="Informations">
            <div className="grid grid-cols-2 gap-2">
              <InfoBox label="Type" value={<Badge className={`${typeMeta.bg} ${typeMeta.text} ${typeMeta.border}`}>{typeMeta.label}</Badge>} />
              <InfoBox label="Créée le" value={fmtDate(txn.created_at)} mono />
              {txn.fulfilled_at && <InfoBox label="Validée le" value={fmtDate(txn.fulfilled_at)} mono />}
              {txn.duration_seconds != null && <InfoBox label="Durée checkout" value={`${txn.duration_seconds}s`} mono />}
              {txn.raffle_title && <InfoBox label="Tirage" value={txn.raffle_title} />}
              {meta.quantity && <InfoBox label="Quantité" value={`${meta.quantity} tickets`} />}
              {meta.points_to_credit && <InfoBox label="Crédit ajouté" value={`+£${meta.points_to_credit}`} />}
              {meta.plan_name && <InfoBox label="Plan VIP" value={meta.plan_name} />}
              {meta.promo_code && <InfoBox label="Code promo" value={<Badge className="bg-emerald-100 text-emerald-700">{meta.promo_code}</Badge>} />}
              {meta.promo_discount && <InfoBox label="Remise promo" value={`-£${meta.promo_discount}`} />}
              {meta.payment_method_label && !isManual && <InfoBox label="Méthode de paiement" value={<Badge className="bg-purple-100 text-purple-700">{meta.payment_method_label}</Badge>} />}
              {meta.reason && <InfoBox label="Motif (admin)" value={meta.reason} />}
              <InfoBox label="Mode passerelle" value={<Badge variant="outline">{txn.gateway_mode || 'direct'}</Badge>} />
              {txn.pay_currency && <InfoBox label="Crypto payée" value={<Badge className="bg-orange-100 text-orange-700 font-mono">{txn.pay_currency.toUpperCase()}</Badge>} />}
              {txn.pay_amount && <InfoBox label="Montant crypto" value={`${txn.pay_amount} ${(txn.pay_currency || '').toUpperCase()}`} mono />}
              {txn.pay_address && <InfoBox label="Adresse de paiement" value={<code className="text-[10px] break-all">{txn.pay_address}</code>} />}
              {txn.actually_paid && <InfoBox label="Reçu réellement" value={`${txn.actually_paid} ${(txn.pay_currency || '').toUpperCase()}`} mono />}
              {txn.outcome_amount && <InfoBox label="Payout (USDT)" value={`${txn.outcome_amount} ${(txn.payout_currency || 'USDTTRC20').toUpperCase()}`} mono />}
              {txn.stripe_mode && <InfoBox label="Mode Stripe" value={<Badge variant="outline">{txn.stripe_mode}</Badge>} />}
              {txn.callback_sent !== undefined && <InfoBox label="Callback" value={txn.callback_sent ? '✅ Reçu' : '❌ Non reçu'} />}
              {txn.manually_fulfilled_at && <InfoBox label="Validé manuellement" value={fmtDate(txn.manually_fulfilled_at)} mono />}
              {txn.resynced_at && <InfoBox label="Récupéré le" value={fmtDate(txn.resynced_at)} mono />}
              {txn.auto_synced_at && <InfoBox label="Auto-sync" value={fmtDate(txn.auto_synced_at)} mono />}
            </div>
          </Section>

          {txn.ticket_numbers?.length > 0 && (
            <Section title="Numéros de tickets">
              <div className="flex flex-wrap gap-1.5">
                {txn.ticket_numbers.map((tk, i) => <Badge key={i} className="bg-gold-gradient text-rolex-green border-none font-mono text-xs">{tk}</Badge>)}
              </div>
            </Section>
          )}

          {Object.keys(meta).length > 0 && (
            <Section title="Métadonnées brutes">
              <pre className="bg-white p-3 rounded-lg text-[10px] font-mono overflow-x-auto border border-gold/20 max-h-40 overflow-y-auto">{JSON.stringify(meta, null, 2)}</pre>
            </Section>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}

function Section({ title, children }) {
  return (
    <div>
      <p className="text-[10px] uppercase tracking-wider text-rolex-green/55 font-bold mb-1.5">{title}</p>
      {children}
    </div>
  );
}

function InfoBox({ label, value, mono = false }) {
  return (
    <div className="bg-white border border-gold/15 rounded-lg p-2">
      <p className="text-[9px] uppercase tracking-wider text-rolex-green/55 font-bold mb-0.5">{label}</p>
      <div className={`text-xs text-rolex-green ${mono ? 'font-mono' : 'font-semibold'}`}>{value}</div>
    </div>
  );
}

function UserDrillDown({ data, onClose, onSelectTxn }) {
  const u = data.user;
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-3xl max-h-[88vh] overflow-y-auto bg-cream">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><UserIcon className="w-5 h-5 text-gold-dark" /> {u.first_name} {u.last_name}</DialogTitle>
          <DialogDescription>{u.email}{u.phone ? ` · ${u.phone}` : ''}</DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <MiniStatCard label="Total dépensé" value={fmt(data.stats.total_spent)} tone="emerald" />
            <MiniStatCard label="Réussies" value={data.stats.successful_count} tone="emerald" />
            <MiniStatCard label="Échouées" value={data.stats.failed_count} tone={data.stats.failed_count > 0 ? 'red' : 'neutral'} />
            <MiniStatCard label="Solde actuel" value={fmt(data.stats.current_balance)} />
          </div>

          <p className="font-bold text-rolex-green pt-2 border-t border-gold/20">Toutes les transactions ({data.transactions.length})</p>
          <div className="space-y-2 max-h-[400px] overflow-y-auto">
            {data.transactions.map((t, i) => {
              const statusKey = getStatusKey(t);
              const statusMeta = STATUS_META[statusKey];
              const typeMeta = getTypeMeta(t);
              const Icon = typeMeta.icon;
              return (
                <button key={t._id || i} type="button" onClick={() => onSelectTxn(t)} className="w-full text-left p-3 rounded-lg border border-gold/20 hover:border-gold/50 hover:bg-cream/50 transition-colors flex items-center gap-3">
                  <div className={`w-9 h-9 rounded-lg ${typeMeta.bg} flex items-center justify-center`}><Icon className={`w-4 h-4 ${typeMeta.text}`} /></div>
                  <div className="flex-1 min-w-0">
                    <p className="font-bold text-sm text-rolex-green truncate">{t.raffle_title || typeMeta.label}</p>
                    <p className="text-[10px] text-rolex-green/55 font-mono">{fmtDate(t.created_at)}</p>
                  </div>
                  <Badge className={`text-[10px] ${statusMeta.cls} text-white border-none`}>{statusMeta.label}</Badge>
                  <span className="font-mono font-bold text-sm text-rolex-green w-20 text-right">{fmt(t.amount_gbp)}</span>
                </button>
              );
            })}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function PerRaffleRow({ r, onView, onFilter }) {
  const s = r.stats;
  const fillPct = r.max_tickets > 0 ? Math.min(100, (r.sold_tickets / r.max_tickets) * 100) : 0;
  return (
    <div className="p-4 hover:bg-cream/40 transition-colors flex items-center gap-4 flex-wrap">
      {r.image_url ? (
        <img src={r.image_url} alt={r.title} className="w-16 h-16 object-cover rounded-xl border border-gold/30 shrink-0" />
      ) : (
        <div className="w-16 h-16 rounded-xl bg-gold/10 border border-gold/20 flex items-center justify-center shrink-0">
          <Trophy className="w-7 h-7 text-gold-dark opacity-40" />
        </div>
      )}
      <div className="flex-1 min-w-[240px]">
        <div className="flex items-center gap-2 flex-wrap mb-1">
          <p className="font-bold text-rolex-green text-sm">{r.title || '(sans titre)'}</p>
          {!r.enabled && <Badge variant="outline" className="text-[9px] text-stone-500 border-stone-300">Désactivé</Badge>}
          {r.coming_soon && <Badge className="bg-amber-500 text-white text-[9px]">Bientôt</Badge>}
          {r.status === 'closed' && <Badge className="bg-stone-500 text-white text-[9px]">Clôturé</Badge>}
        </div>
        <div className="flex items-center gap-3 text-[10px] text-rolex-green/65 flex-wrap">
          <span>Prix : <strong className="text-rolex-green font-mono">{fmt(r.ticket_cost)}</strong></span>
          <span>·</span>
          <span><UsersIcon className="w-3 h-3 inline" /> {s.unique_participants} clients</span>
        </div>
        <div className="mt-1.5 max-w-[260px]">
          <div className="flex justify-between text-[9px] text-rolex-green/55 font-mono mb-0.5">
            <span>{r.sold_tickets.toLocaleString()} / {r.max_tickets.toLocaleString()}</span>
            <span>{fillPct.toFixed(1)}%</span>
          </div>
          <div className="h-1.5 bg-cream rounded-full overflow-hidden border border-gold/20">
            <div className="h-full bg-gold-gradient" style={{ width: `${fillPct}%` }} />
          </div>
        </div>
      </div>
      <div className="text-right shrink-0 min-w-[140px]">
        <p className="text-[9px] uppercase tracking-wider text-rolex-green/55 font-bold">Revenu total</p>
        <p className="font-mono text-xl font-extrabold text-emerald-700">{fmt(s.total_revenue)}</p>
        <p className="text-[10px] text-rolex-green/55 mt-0.5">{s.total_transactions} ventes ({s.tickets_sold} tickets)</p>
        {(s.pending_count > 0 || s.failed_count > 0) && (
          <div className="flex gap-1 justify-end mt-1">
            {s.pending_count > 0 && (
              <Badge className="bg-amber-100 text-amber-700 border-amber-200 text-[9px] gap-1"><Clock className="w-2.5 h-2.5" />{s.pending_count}</Badge>
            )}
            {s.failed_count > 0 && (
              <Badge className="bg-red-100 text-red-700 border-red-200 text-[9px] gap-1"><XCircle className="w-2.5 h-2.5" />{s.failed_count}</Badge>
            )}
          </div>
        )}
      </div>
      <div className="flex flex-col gap-1 shrink-0">
        <Button size="sm" variant="outline" onClick={onView} className="border-gold/40 text-gold-dark hover:bg-gold/10 gap-1 h-8" data-testid={`raffle-view-${r._id}`}>
          <Eye className="w-3.5 h-3.5" /> Détails
        </Button>
        <Button size="sm" variant="outline" onClick={onFilter} className="border-emerald-300 text-emerald-700 hover:bg-emerald-50 gap-1 h-8" data-testid={`raffle-filter-${r._id}`}>
          <Filter className="w-3.5 h-3.5" /> Transactions
        </Button>
      </div>
    </div>
  );
}

function RaffleDetailDialog({ raffle, onClose, onFilter }) {
  const s = raffle.stats;
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto bg-cream">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><Trophy className="w-5 h-5 text-gold-dark" /> {raffle.title}</DialogTitle>
          <DialogDescription>Vue détaillée des ventes</DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          {raffle.image_url && <img src={raffle.image_url} alt={raffle.title} className="w-full h-48 object-cover rounded-xl border border-gold/30" />}
          <div className="grid grid-cols-2 gap-3">
            <MiniStatCard label="Revenu total" value={fmt(s.total_revenue)} tone="emerald" />
            <MiniStatCard label="Tickets vendus" value={s.tickets_sold} />
            <MiniStatCard label="Clients uniques" value={s.unique_participants} />
            <MiniStatCard label="Transactions" value={s.total_transactions} />
          </div>
          <Section title="Répartition revenus">
            <div className="space-y-2">
              <div className="flex justify-between items-center p-2 rounded-lg bg-white border border-gold/20">
                <span className="text-sm flex items-center gap-2"><CreditCard className="w-4 h-4 text-emerald-600" /> Stripe (carte)</span>
                <span className="font-mono font-bold text-emerald-700">{fmt(s.stripe_revenue)} <span className="text-xs opacity-65">· {s.stripe_tickets} tickets</span></span>
              </div>
              <div className="flex justify-between items-center p-2 rounded-lg bg-white border border-gold/20">
                <span className="text-sm flex items-center gap-2"><Wallet className="w-4 h-4 text-amber-600" /> Crédit (wallet)</span>
                <span className="font-mono font-bold text-amber-700">{fmt(s.credit_revenue)} <span className="text-xs opacity-65">· {s.credit_tickets} tickets</span></span>
              </div>
            </div>
          </Section>
          {(s.pending_count > 0 || s.failed_count > 0) && (
            <Section title="Paiements non aboutis">
              <div className="space-y-2">
                {s.pending_count > 0 && (
                  <div className="flex justify-between items-center p-2 rounded-lg bg-amber-50 border border-amber-200">
                    <span className="text-sm flex items-center gap-2"><Clock className="w-4 h-4 text-amber-600" /> En attente / abandonnés</span>
                    <span className="font-mono font-bold text-amber-700">{fmt(s.pending_amount)} · {s.pending_count}</span>
                  </div>
                )}
                {s.failed_count > 0 && (
                  <div className="flex justify-between items-center p-2 rounded-lg bg-red-50 border border-red-200">
                    <span className="text-sm flex items-center gap-2"><XCircle className="w-4 h-4 text-red-600" /> Échoués / annulés</span>
                    <span className="font-mono font-bold text-red-700">{fmt(s.failed_amount)} · {s.failed_count}</span>
                  </div>
                )}
              </div>
            </Section>
          )}
          <Button onClick={onFilter} className="btn-gold w-full gap-2" data-testid="raffle-detail-filter-btn">
            <Filter className="w-4 h-4" /> Voir toutes les transactions de ce tirage
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
